// One session, hand-authored, that every skin renders UNCHANGED.
//
// A skin with its own fixture cannot be compared to anything: the moment the
// terminal storybook writes its own rows, "the same facts in both registers"
// stops being checkable and becomes a claim. So this file is data and nothing
// else — no element, no skin import, no layout — and both integration
// storybooks walk it through `TurnSlots.Skin.make`.
//
// ## Why it is invented rather than captured
//
// Because a capture cannot exercise the vocabulary. Measured on this host's
// corpus: `plan` appears twice in 24,975 records, `attachment` never, and
// `thought` is 0 of 43,689 harness records — the producer arm exists but
// nothing has emitted reasoning here yet. A "representative" capture would
// therefore prove out four slots and leave the other eight unrendered in both
// skins, which is the opposite of a contract proof.
//
// What IS taken from the corpus is the shape: the order below is the order a
// real codex turn arrives in, and the tool rows carry the field set the wire
// actually delivers (`label`, `result`, `exitCode`, `durationMs`).
//
// ## Three lengths are deliberate, and they are the point
//
// Invented lengths are how a layout looks right in review and wrong in
// production, so this fixture carries, on purpose:
//
//   - a long prose block — multi-paragraph, with a list and inline code, past
//     the height where a native reply starts to need a measured row;
//   - a wide tool output — a line far past any column, which is what finds a
//     `pre` that scrolls the page instead of itself;
//   - a failing result, and a failed call inside a fold, because the fold
//     exists so a reader can skip what went right and a hidden failure is the
//     one outcome they came back for.
//
// A skin that looks good on this fixture and bad on a session is a skin with
// a length bug, and that is what it is for.

import { TurnSlots } from "./slots";

// A fixture action carries the SVG data that every skin passes to IconButton.
// Imported .svg files can become URL paths in the storybook bundler.
const ACTION_STROKE = 'fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"';
const COPY_ICON =
  `<svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><g ${ACTION_STROKE}><rect x="5" y="5" width="8" height="9" rx="1"/><path d="M3 11V3a1 1 0 0 1 1-1h7"/></g></svg>`;
const RETRY_ICON =
  `<svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><g ${ACTION_STROKE}><path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9"/><path d="M13.5 2v3.2h-3.2"/></g></svg>`;

export namespace ThreadFixture {
  /** One rendered row: a slot name and the props for THAT slot. Written
   * through `at()` rather than annotated, so the literal slot names survive
   * into the type and the coverage proof below has something to read. */
  export type Entry = {
    [S in TurnSlots.Slot]: { slot: S; props: TurnSlots.Props[S] };
  }[TurnSlots.Slot];

  function at<S extends TurnSlots.Slot>(
    slot: S,
    props: TurnSlots.Props[S],
  ): { slot: S; props: TurnSlots.Props[S] } {
    return { slot, props };
  }

  const WIDE_OUTPUT = [
    "   Compiling tento-pane-runtime v0.1.0 (/Users/james/workspace/tento/tento-pane-runtime)",
    "warning: unused variable: `lane_id` --> tento-pane-runtime/src/dispatch.rs:412:23",
    "    Finished `test` profile [unoptimized + debuginfo] target(s) in 41.62s",
    "     Running unittests src/lib.rs (/Users/james/ssd-mount/cargo-target/debug/deps/tento_pane_runtime-9f2c1ab4e7d60358)",
    "running 212 tests",
    "test dispatch::tests::a_dropped_lane_settles_its_open_turn ... ok",
    "test dispatch::tests::records_interleave_by_clock_not_by_seq ... ok",
    "test queue::tests::a_queued_unit_survives_a_restart ... ok",
    "test result: ok. 212 passed; 0 failed; 0 ignored; 0 measured; 0 filtered out; finished in 2.41s",
  ].join("\n");

  const LONG_PROSE = [
    "Fixed, and the cause was not where the symptom was. `visibleRecords` "
      + "merged every lane into one list ordered by `seq`, which Rust computes "
      + "**per pane** — so `ctl` numbering 1–14 and `news` numbering 1–114 "
      + "interleaved, and `closeTurn()` shredded one lane's turn across the "
      + "other's prompts.",
    "The merge is now k-way over already-ordered lane streams, keyed on a "
      + "parsed timestamp rather than a compared string. Two measured hazards "
      + "made that necessary:",
    "- 34,392 records carry microsecond stamps and 38 carry milliseconds, so a "
      + "string compare puts `.200Z` after `.200437Z`;\n"
      + "- four records in session `14925` go backwards *inside* their own "
      + "lane, where `seq` is authoritative and the clock is not.",
    "Clamping each stamp to at least its predecessor's answers both at once: a "
      + "lane keeps its own order no matter what its clock did, and only the "
      + "interleave between lanes reads the time. The regression fixture is "
      + "session `52738`, which used to file nine WebSearches under "
      + "`(lane.declare \"news\" …)`.",
  ].join("\n\n");

  /** The session, in arrival order. Read it top to bottom: a prompt, the
   * model's reasoning, a plan, the work, an approval it had to ask for, the
   * one call that failed, the answer, and the bar that attributes it. */
  export const TURN = [
    at("prompt", {
      text: "the transcript files one lane's tool calls under another lane's "
        + "prompt on ::history — figure out why and fix it",
    }),

    at("thought", {
      text: "Two lanes are live, so the merge is the suspect before the "
        + "renderer is. If `seq` is per-pane then sorting on it interleaves "
        + "two independent counters, which would look exactly like this — "
        + "turns shredded across each other rather than misplaced by one.",
      summary: "Thought for 12s",
      expanded: false,
    }),

    at("plan", {
      steps: [
        { step: "confirm `seq` is per-pane, not per-session", status: "done" },
        { step: "merge lanes on a parsed clock instead", status: "done" },
        { step: "keep turn state per lane so `closeTurn` cannot cross", status: "running" },
        { step: "re-check session 52738, the regression fixture", status: "pending" },
      ],
    }),

    at("tool", {
      tool: "search",
      verb: "Search",
      title: "seq",
      note: "31 matches in 12 files",
      status: "done",
    }),

    at("tool", {
      tool: "read",
      verb: "Read",
      title: "tento-pane-runtime/src/dispatch.rs",
      note: "412 lines",
      status: "done",
    }),

    at("attachment", {
      label: "session-52738.trace.ljson",
      kind: "file",
      meta: "1.4 MB · 34,430 records",
    }),

    at("term-block", {
      text: "fn lane_clock(lane: &Lane) -> Vec<i64> {\n"
        + "    let mut last = 0;\n"
        + "    lane.records.iter().map(|r| { last = r.at.max(last); last }).collect()\n"
        + "}",
      language: "rust",
    }),

    at("ask", {
      title: "Run the pane-runtime test suite?",
      command: "cargo test -q -p tento-pane-runtime",
      options: [
        { optionId: "allow_once", name: "Allow once", kind: "allow_once" },
        { optionId: "allow_always", name: "Allow always", kind: "allow_always" },
        { optionId: "reject_once", name: "Reject", kind: "reject_once" },
      ],
      state: "decided",
      decidedOptionId: "allow_once",
    }),

    // The wide one. 212 lines of cargo output folded to a `+N lines`
    // affordance, with a single line past any sane column width.
    at("tool", {
      tool: "execute",
      verb: "Ran",
      title: "cargo test -q -p tento-pane-runtime",
      note: "exit 0 · 41.6s",
      detail: { text: WIDE_OUTPUT, language: "plain" },
      expanded: true,
      status: "done",
    }),

    at("tool", {
      tool: "edit",
      verb: "Update",
      title: "tento-node-registry/tento-spool.node/html/routes/stream-items.ts",
      note: "+38 −11",
      detail: {
        text: "@@ -60,7 +60,21 @@\n"
          + "-  return lanes.flatMap((lane) => lane.records).sort((a, b) => a.seq - b.seq);\n"
          + "+  const cursors = lanesFor(instance, laneId).map((lane) => ({\n"
          + "+    lane,\n"
          + "+    clock: laneClock(lane),\n"
          + "+    index: 0,\n"
          + "+  }));\n"
          + "   // the interleave reads the clock; the lane keeps its own order\n",
        language: "diff",
      },
      status: "done",
    }),

    at("note", { face: "rule", label: "retry" }),

    // The failure, deliberately NOT last: a fold that only ever has to survive
    // a failure in its final row is a fold that has not been tested.
    at("tool", {
      tool: "execute",
      verb: "Ran",
      title: "deno run -A build.deno.ts",
      note: "exit 1 · 0.9s",
      detail: {
        text: "error: The symbol \"bindPrimaryCell\" has already been declared\n"
          + "    at routes/sidebar.ts:328:10",
        language: "plain",
      },
      status: "failed",
    }),

    at("tool", {
      tool: "execute",
      verb: "Ran",
      title: "rm -rf ~/.tento-sh/sessions",
      note: "not run",
      status: "declined",
    }),

    at("note", { face: "text", text: "rebuilding after the duplicate declaration", active: false }),

    at("kv-grid", {
      pairs: [
        ["session", "52738"],
        ["lanes", "ctl, news"],
        ["records", "34,430"],
        ["out of order", "4 (within lane)"],
        ["ms-precision stamps", "38"],
      ],
    }),

    at("prose", { text: LONG_PROSE }),

    at("result", {
      status: "error",
      text: "1 test failed after the merge change",
      detail: "dispatch::tests::records_interleave_by_clock_not_by_seq\n"
        + "  left:  [ctl#1, news#1, ctl#2]\n"
        + "  right: [ctl#1, ctl#2, news#1]",
      expanded: false,
    }),

    // Live at the tail: the turn is waiting on the reader, which is the state
    // a settled-turn projection must NOT be handed.
    at("ask", {
      title: "Apply the fix to `closeTurn` and re-run?",
      command: "cargo test -q -p tento-pane-runtime dispatch::",
      options: [
        { optionId: "allow_once", name: "Allow once", kind: "allow_once" },
        { optionId: "reject_once", name: "Reject", kind: "reject_once" },
      ],
      state: "pending",
    }),

    at("bar", {
      actions: [
        { id: "copy", label: "Copy", icon: COPY_ICON },
        { id: "retry", label: "Retry", icon: RETRY_ICON },
      ],
      meta: [
        { key: "took", value: "1m 51s" },
        { key: "output", value: "3.6k tokens" },
      ],
      faces: [
        { id: "1", tone: "done", label: "Search seq" },
        { id: "2", tone: "done", label: "Read dispatch.rs" },
        { id: "3", tone: "done", label: "Ran cargo test -q -p tento-pane-runtime" },
        { id: "4", tone: "done", label: "Update stream-items.ts" },
        { id: "5", tone: "failed", label: "Ran deno run -A build.deno.ts" },
        { id: "6", tone: "declined", label: "Ran rm -rf ~/.tento-sh/sessions" },
      ],
    }),
  ] as const;

  /** Which slots the session above actually renders. */
  type Covered = typeof TURN[number]["slot"];

  /** The coverage proof, in the contract's own idiom: this line stops
   * compiling the moment a slot exists that the fixture never renders — which
   * is the only way "every skin, every slot, side by side" stays true after
   * the vocabulary grows. Nothing reads it at runtime. */
  type _EverySlotAppears = TurnSlots.Implements<TurnSlots.Slot, Covered>;

  /** The session as elements, in one skin. Both integration storybooks call
   * THIS rather than writing their own loop, so neither can quietly skip a
   * row the other renders. */
  export function renderWith(skin: TurnSlots.Skin): HTMLElement[] {
    return TURN.map((entry) => skin.make(entry.slot, entry.props));
  }
}
